import { defineStore } from 'pinia';
import { computed, reactive } from 'vue';
import { useLocalStorage } from '@vueuse/core';
import { useUserStore } from './userStore';
import { useSessionStore } from './sessionStore';
import { useUIStore } from '@/stores/uiStore';
import { useThemeStore } from './themeStore';
import { useGameStore } from './gameStore';
import { differenceInMilliseconds } from 'date-fns';
import { GamePlayState } from '@/types/gamePlay';
import { GameAssets } from '@/types/assets';
import { useGameAudioStore } from '@/stores/gameAudioStore';
import { navigateToHome } from '@/router';
import FetchDataClient from '@/api/FetchDataClient';
import { useTimerStore } from '@/stores/timerStore';
import { useGamesRefresh } from '@/composables/useGamesRefresh';
import { useGameProgressStore } from '@/stores/gameProgressStore';

// Initialize with stored token if available
let initialToken = null;
try {
  const storedAuth = localStorage.getItem('user.auth');
  if (storedAuth) {
    const auth = JSON.parse(storedAuth);
    initialToken = auth.token;
  }
} catch (e) {
  console.warn('Failed to load initial token:', e);
}

const api = new FetchDataClient(initialToken);

interface GameData {
  id: number;
  gameName: string;
  gameId: number;
  level: number;
  max_level: number;
  mode: string;
  title: string;
  backgroundImage?: string;
  backgroundRepeat?: boolean;
  assets: {
    images: Record<string, string>;
    audio: Record<string, string>;
  };
}

interface SaveTrialResultsData {
  game_id: number | null;
  play_id: number;
  round_id: number | null;
  level: number | null;
  trial: number;
  round: number;
  correct: number;
  response_time: number;
}

interface TrialResultsData {
  playId: number;
  playStart: number;
  tickets?: number;
  correctThisRound: boolean;
  gamePlayComplete: boolean;
  remainingTrials: boolean;
}

export const useGamePlayStore = defineStore('gamePlay', () => {
  const storedLastClickedGame = useLocalStorage<string | null>(
    'lastClickedGame',
    null
  );
  const state = reactive<GamePlayState>({
    games: [],
    lastClickedGame: storedLastClickedGame.value || null,
    currentStep: null,
    timer: null,
    timerDuration: 5,
    answerTimerDuration: 5,
    stopTimer: false,
    loading: false,
    gameId: null,
    roundId: null,
    currentRound: 1,
    currentTrial: 1,
    rounds: 5,
    trials: 2,
    level: 1,
    maxLevel: 50,
    gameMode: 'size',
    currentGameLevel: null,
    hints: [],
    progress: 0,
    query_colors: 0,
    scoreDownIncorrect: 0,
    scoreUpTrial: 0,
    textPlayHints: '',
    trialWasCorrect: false,
    assets: {
      images: {},
      audio: {},
    },
    trialsComplete: 0,
    trialsCorrect: 0,
    previousLocations: [],
    trialStatus: [],
    score: 0,
    currentGame: null,
    inMission: false,
    movedUpALevel: false,
    changedRound: false,
    currentGameScore: 0,
    currentGamePlayHints: {},
    answerSequence: [],
    correctAnswers: [],
    typedChars: '',
    modalMessage: null,
    correctThisRound: 0,
    playStart: 0,
    tickets: 0,
    instructions: [],
  });

  // Computed getters for all properties
  const games = computed(() => state.games);
  const lastClickedGame = computed(() => state.lastClickedGame);
  const currentStep = computed(() => state.currentStep);
  const timer = computed(() => state.timer);
  const timerDuration = computed(() => state.timerDuration);
  const answerTimerDuration = computed(() => state.answerTimerDuration);
  const stopTimer = computed(() => state.stopTimer);
  const loading = computed(() => state.loading);
  const gameId = computed(() => state.gameId);
  const roundId = computed(() => state.roundId);
  const currentRound = computed(() => state.currentRound);
  const currentTrial = computed(() => state.currentTrial);
  const rounds = computed(() => state.rounds);
  const trials = computed(() => state.trials);
  const level = computed(() => state.level);
  const maxLevel = computed(() => state.maxLevel);
  const gameMode = computed(() => state.gameMode);

  const hints = computed(() => state.hints);
  const progress = computed(() => state.progress);
  const query_colors = computed(() => state.query_colors);
  const scoreUpHint = computed(() => state.scoreUpHint);
  const scoreDownIncorrect = computed(() => state.scoreDownIncorrect);
  const scoreUpTrial = computed(() => state.scoreUpTrial);
  const textPlayHints = computed(() => state.textPlayHints);
  const trialWasCorrect = computed(() => state.trialWasCorrect);
  const assets = computed(() => state.assets);
  const trialsComplete = computed(() => state.trialsComplete);
  const trialsCorrect = computed(() => state.trialsCorrect);
  const previousLocations = computed(() => state.previousLocations);
  const trialStatus = computed(() => state.trialStatus);
  const score = computed(() => state.score);
  const currentGame = computed(() => state.currentGame);
  const inMission = computed(() => state.inMission);
  const movedUpALevel = computed(() => state.movedUpALevel);
  const currentGameScore = computed(() => state.currentGameScore);
  const answerSequence = computed(() => state.answerSequence);
  const correctAnswers = computed(() => state.correctAnswers);
  const typedChars = computed(() => state.typedChars);
  const modalMessage = computed(() => state.modalMessage);
  const correctThisRound = computed(() => state.correctThisRound);
  const instructions = computed(() => state.instructions);

  // Existing computed properties
  const currentGameObject = computed(
    () => state.games.find((game) => game.id === state.gameId) || {}
  );

  const remainingTrials = computed(() => state.currentTrial < state.trials);
  const remainingRounds = computed(() => state.currentRound < state.rounds);
  const gamePlayComplete = computed(
    () => !remainingTrials.value && !remainingRounds.value
  );
  const isFirstPlay = computed(
    () => state.currentTrial === 1 && state.currentRound === 1
  );

  const activeComponent = computed(() => {
    const uiStore = useUIStore();
    const gameSteps: Record<string, string> = {
      loading: 'Loading',
      introduction: 'Introduction',
      instructions: 'Instructions',
      demo: 'Demo',
      play: 'Play',
      success: 'Success',
      failure: 'Failure',
    };
    return gameSteps[uiStore.state.currentStep || 'introduction'];
  });

  // Actions
  function setGameId(value: number) {
    state.gameId = value;
  }

  function setRoundId(value: number) {
    state.roundId = value;
  }

  function setCurrentRound(value: number) {
    state.currentRound = value;
  }

  function setCurrentTrial(value: number) {
    state.currentTrial = value;
  }

  function setLevel(value: number) {
    state.level = value;
  }

  function setMaxLevel(value: number) {
    state.maxLevel = value;
  }

  function setGameMode(value: string) {
    state.gameMode = value;
  }

  function setCurrentGame(value: string | null) {
    if (value && typeof value !== 'string') {
      console.warn(
        '[gamePlayStore] Non-string currentGame value detected:',
        value,
        'typeof =',
        typeof value
      );
    }
    state.currentGame = value;
  }

  function setScore(value: number) {
    state.score = value;
  }

  function setCurrentStep(value: string | null) {
    state.currentStep = value;
  }

  function setTimer(value: number | null) {
    state.timer = value;
  }

  function setTimerDuration(value: number) {
    state.timerDuration = value;
  }

  function setAnswerTimerDuration(value: number) {
    state.answerTimerDuration = value;
  }

  function setHints(value: Array<string | Record<string, unknown>>) {
    state.hints = value;
  }

  function setTextPlayHints(value: string) {
    state.textPlayHints = value;
  }

  function setTrials(value: number) {
    state.trials = value;
  }

  function setRounds(value: number) {
    state.rounds = value;
  }

  function setAssets(value: GameAssets) {
    state.assets = value;
  }

  function setMovedUpALevel(value: boolean) {
    state.movedUpALevel = value;
  }

  function setTrialWasCorrect(value: boolean) {
    state.trialWasCorrect = value;
  }

  function setTrialsCorrect(value: number) {
    state.trialsCorrect = value;
  }

  function incrementTrialsCorrect(): void {
    state.trialsCorrect++;
  }

  function setLoading(value: boolean) {
    state.loading = value;
  }

  function setTrialsComplete(value: number) {
    state.trialsComplete = value;
  }

  function setPreviousLocations(
    value: Array<{ x: number; y: number } | number>
  ) {
    state.previousLocations = value;
  }

  function addToPreviousLocations(
    data: { x: number; y: number } | number
  ): void {
    // Handle both object format (for other games) and number format (for TheDecoder)
    if (typeof data === 'number') {
      // For TheDecoder game, just store the location index
      state.previousLocations.push(data);
    } else {
      state.previousLocations.push(data);
    }
  }

  function clearPreviousLocations(): void {
    state.previousLocations = [];
  }

  function setTrialStatus(value: string) {
    state.trialStatus.push(value);
  }

  function clearTrialStatus(): void {
    state.trialStatus = [];
  }

  function setCorrectAnswers(value: Array<string | number>) {
    state.correctAnswers = value;
  }

  function addToCorrectAnswers(value: string | number): void {
    state.correctAnswers.push(value);
  }

  function resetCorrectAnswers(): void {
    state.correctAnswers = [];
  }

  function setAnswerSequence(value: Array<string | number>) {
    state.answerSequence = value;
  }

  function addToAnswerSequence(value: string | number): void {
    state.answerSequence.push(value);
  }

  function removeLastNumberAnswerInSequence(): void {
    if (state.answerSequence.length > 0) {
      state.answerSequence.pop();
    }
  }

  function clearAnswerSequence(): void {
    state.answerSequence = [];
  }

  function setTypedChars(value: string): void {
    state.typedChars = value;
  }

  function setModalMessage(
    value: string | Record<string, unknown> | null
  ): void {
    state.modalMessage = value;
  }

  function setCorrectThisRound(value: number): void {
    state.correctThisRound = value;
  }

  function incrementCorrectThisRound(): void {
    state.correctThisRound++;
  }

  function setCurrentGameScore(value: number) {
    state.currentGameScore = value;
  }

  function setQueryColors(value: number) {
    state.query_colors = value;
  }

  function setInMission(value: boolean) {
    state.inMission = value;
  }

  function setGames(value: Array<Record<string, unknown>>) {
    state.games = value;
  }

  function setLastClickedGame(value: string | null) {
    state.lastClickedGame = value;
    storedLastClickedGame.value = value;
  }

  function setStopTimer(value: boolean) {
    state.stopTimer = value;
  }

  function setProgress(value: number) {
    state.progress = value;
  }

  function setScoreDownIncorrect(value: number) {
    state.scoreDownIncorrect = value;
  }

  function setScoreUpTrial(value: number) {
    state.scoreUpTrial = value;
  }

  function setChangedRound(value: boolean) {
    state.changedRound = value;
  }

  function getTrialClasses(index: number): string {
    const status = state.trialStatus[index];
    const classes: string[] = [];
    if (index === state.currentTrial - 1) {
      classes.push('active');
    }
    if (status) classes.push(status);
    return classes.join(' ');
  }

  async function leaveGame(incomplete = false): Promise<void> {
    const userStore = useUserStore();
    const timerStore = useTimerStore();
    const sessionStore = useSessionStore();

    console.log('[leaveGame] Starting leave game process', {
      gameId: state.gameId,
      currentGame: state.currentGame,
      incomplete,
      lastClickedGame: state.lastClickedGame,
    });

    // The lastClickedGame should already be set from when the user clicked on the game
    // We just need to preserve it (not clear it)
    console.log(
      '[leaveGame] Preserving lastClickedGame:',
      state.lastClickedGame
    );

    try {
      // Update user's game level first - but don't block navigation if it fails
      try {
        await updateGameLevel({
          game_id: state.gameId!,
          level: state.level!,
          newTickets: userStore.user?.tickets || 0,
        });
      } catch (apiError) {
        console.error('[leaveGame] Failed to update game level:', apiError);
        // Continue with leaving the game even if API call fails
      }

      if (incomplete && sessionStore.play_id) {
        try {
          await updatePlayLevel({
            gameId: state.gameId!,
            play_id: sessionStore.play_id,
            level: state.level!,
            tickets: userStore.user.tickets,
            stopTime: new Date().getTime(),
          });
        } catch (apiError) {
          console.error('[leaveGame] Failed to update play level:', apiError);
          // Continue with leaving the game even if API call fails
        }
      }
    } catch (error) {
      console.error('[leaveGame] Unexpected error during API calls:', error);
    }

    // Always execute cleanup and navigation, regardless of API call results
    try {
      // Stop music
      const gameAudio = useGameAudioStore();
      gameAudio.stopMusic();

      // Clear out store states
      setCurrentGame(null);
      setCurrentStep(null);
      sessionStore.gameExited = true;
      timerStore.timer = null;
      timerStore.stopTimer = true;

      // Don't clear lastClickedGame - it should remain set to the game that was clicked
      // This ensures we return to the correct game in the slider
      console.log(
        '[leaveGame] Keeping lastClickedGame as:',
        state.lastClickedGame
      );

      console.log('[leaveGame] Navigating to home');
      await navigateToHome();
      console.log('[leaveGame] Navigation completed');
    } catch (navigationError) {
      console.error(
        '[leaveGame] Critical error during cleanup/navigation:',
        navigationError
      );
      // As a last resort, try direct navigation
      window.location.href = '/';
    }
  }

  // Actions
  async function updateGameLevel(data: {
    game_id: number;
    level: number;
    newTickets: number;
  }): Promise<unknown> {
    const sessionStore = useSessionStore();
    console.log('📤 Calling updateGameLevel:', data);

    const response = await api.post(
      `/gameplay/users/set_game_level/${data.game_id}`,
      data,
      getToken()
    );

    // Check if the response indicates game completion
    if (response?.completed_plays !== undefined) {
      console.log(
        '📊 updateGameLevel response has completed_plays:',
        response.completed_plays
      );
      const userStore = useUserStore();
      userStore.setGamesPlayedToday(response.completed_plays);
    } else {
      console.log('⚠️ updateGameLevel response missing completed_plays field');
    }

    return response;
  }

  async function updatePlayLevel(data: {
    gameId: number | null;
    play_id: number;
    level: number;
    tickets: number;
    stopTime: number;
    gameComplete?: boolean;
  }) {
    const gameStore = useGameStore();
    const userStore = useUserStore();
    const gameProgressStore = useGameProgressStore();
    const { refreshGamesIfStale } = useGamesRefresh();

    const previousInMission = gameStore.inMission;
    const missionGamesBeforeSubmit = Array.isArray(gameStore.games)
      ? gameStore.games.length
      : 0;
    const likelyMissionCompletionSubmission =
      previousInMission &&
      data.gameComplete === true &&
      missionGamesBeforeSubmit <= 1;
    try {
      console.log('📤 Calling updatePlayLevel:', data);

      const response = await api.post(
        `/gameplay/game/${data.gameId}/updateGamePlay`,
        data,
        getToken()
      );
      // Mission completion handling + tickets sync
      if (response) {
        if (typeof response.in_mission === 'boolean') {
          gameStore.setInMission(response.in_mission);
        }

        // If we just exited mission mode, sync tickets and refresh available games
        if (
          previousInMission &&
          response.in_mission === false &&
          typeof response.tickets === 'number'
        ) {
          if (userStore.user) {
            userStore.user.tickets = response.tickets;
          }
          gameProgressStore.setTickets(response.tickets);

          // Force-refresh games list to unlock full catalog
          await refreshGamesIfStale({ force: true });
        }

        // If backend awarded mission bonus but we weren't in mission before (edge), still sync tickets
        if (
          typeof response.tickets === 'number' &&
          !previousInMission &&
          typeof response.mission_bonus === 'number' &&
          response.mission_bonus > 0
        ) {
          if (userStore.user) {
            userStore.user.tickets = response.tickets;
          }
          gameProgressStore.setTickets(response.tickets);
        }

        // Always sync tickets from backend as source of truth
        if (typeof response.tickets === 'number') {
          if (userStore.user) {
            userStore.user.tickets = response.tickets;
          }
          gameProgressStore.setTickets(response.tickets);
        }

        // Mission end bonus: always award the configured mission_end from /user payload
        // (stored in gameStore) once the backend has persisted mission completion.
        if (previousInMission && response.in_mission === false) {
          const missionEnd = gameStore.missionEnd;

          if (
            typeof missionEnd === 'number' &&
            Number.isFinite(missionEnd) &&
            missionEnd > 0
          ) {
            userStore.increaseTickets(missionEnd);
          }
        }
      }

      // Check if the response indicates game completion
      if (response?.completed_plays !== undefined) {
        console.log(
          '📊 updatePlayLevel response has completed_plays:',
          response.completed_plays
        );
        const userStore = useUserStore();
        userStore.setGamesPlayedToday(response.completed_plays);
      } else {
        console.log(
          '⚠️ updatePlayLevel response missing completed_plays field'
        );
      }

      return response;
    } catch (error) {
      console.error('Error updating play level:', error);

      if (likelyMissionCompletionSubmission) {
        console.warn(
          '[updatePlayLevel] Mission completion API failed; keeping client in Free Play while API queue retries.'
        );

        gameStore.setInMission(false);

        try {
          await refreshGamesIfStale({ force: true });
        } catch (refreshError) {
          console.error(
            '[updatePlayLevel] Failed to refresh games after optimistic mission exit:',
            refreshError
          );
        }

        // Preserve optimistic Free Play state even if immediate refresh is stale.
        if (gameStore.inMission) {
          gameStore.setInMission(false);
        }

        return {
          in_mission: false,
          mission_completed: true,
          queued: true,
          optimistic: true,
        };
      }
      throw error;
    }
  }

  async function processTrialAndAdvance(
    createNewRound: boolean = false,
    data: TrialResultsData
  ): Promise<void> {
    const trialStop = new Date();
    const responseTime = differenceInMilliseconds(
      trialStop,
      data.playStart || state.playStart
    );

    console.log('¢¢¢ roundId', state.roundId);
    console.log(
      '🎮 processTrialAndAdvance - gamePlayComplete:',
      data.gamePlayComplete
    );

    const response = await api.post(
      '/gameplay/process_trial',
      {
        roundId: state.roundId,
        gameId: state.gameId,
        playId: data.playId,
        trialWasCorrect: state.trialWasCorrect,
        start: data.playStart || state.playStart,
        response_time: responseTime,
        correct: state.trialWasCorrect,
        stop: trialStop.getTime(),
        accuracy: state.trialWasCorrect ? 1 : 0,
        currentLevel: state.level,
        tickets: data.tickets,
        currentTrial: state.currentTrial,
        totalTrials: state.trials,
        createNewRound: createNewRound,
        correctThisRound: data.correctThisRound,
        remainingTrials: data.remainingTrials,
        gamePlayComplete: data.gamePlayComplete,
      },
      getToken()
    );

    // Handle games counter update with proper priority
    const userStore = useUserStore();

    // Priority 1: Use API response if available (most authoritative)
    if (response?.completed_plays !== undefined) {
      console.log(
        '📊 Setting games count from API response:',
        response.completed_plays
      );
      userStore.setGamesPlayedToday(response.completed_plays);
    }
    // Priority 2: If game is complete and API didn't provide count, increment locally
    else if (data.gamePlayComplete) {
      console.log('📊 Game complete, incrementing local counter');
      userStore.incrementGamesPlayedToday();
    }

    return response;
  }

  function startNewRound() {
    console.log(
      '/// Starting new round. Current trial:',
      state.currentTrial,
      'Total trials:',
      state.trials
    );

    // Always increment trial
    const nextTrial = state.currentTrial + 1;

    if (nextTrial > state.trials) {
      // If next trial would exceed trials per round, start new round
      setCurrentRound(state.currentRound + 1);
      setCurrentTrial(1);
      setTrialsCorrect(0); // Reset trials correct counter for new round
      clearTrialStatus();
      console.log('Started new round:', state.currentRound);
    } else {
      // Otherwise just increment the trial counter
      setCurrentTrial(nextTrial);
      console.log('Incremented trial to:', state.currentTrial);
    }
  }

  /**
   * Fetch the game level, settings, and user's current level.
   * @param data
   */

  async function getUserLevelAndSettings() {
    // Get the token which will be used for the request
    const token = getToken();

    // Log important context information
    console.log('getUserLevelAndSettings called with:', {
      gameId: state.gameId,
      hasToken: !!token,
    });

    if (!state.gameId) {
      console.error(
        'getUserLevelAndSettings called with no gameId set in state!'
      );
      throw new Error('No gameId set in state');
    }

    try {
      // Pass token explicitly to the API call for better reliability
      const result = await api.get(
        `/gameplay/game/${state.gameId}/get_settings`,
        token
      );
      return result;
    } catch (error) {
      console.error('Error in getUserLevelAndSettings:', error);
      throw error;
    }
  }

  async function startGamePlay(data: { gameId: number; playId: number }) {
    const token = getToken();
    console.log('Starting game play', data);
    return api.post(
      `/gameplay/game/${data.gameId}`,
      { play_id: data.playId },
      token
    );
  }

  async function setGameLevelAndSaveTrialResults(
    createNewRound: boolean = false,
    data: TrialResultsData
  ): Promise<void> {
    const trialStop = new Date();
    const responseTime = differenceInMilliseconds(
      trialStop,
      data.playStart || state.playStart
    );

    try {
      await api.post(
        `/gameplay/set_trial_results/${state.gameId}`,
        {
          game_id: state.gameId,
          level: state.level,
          newTickets: data.tickets,
          response_time: responseTime,
          round_id: state.roundId,
          play_id: data.play_id,
          correct: data.trialWasCorrect ?? state.trialWasCorrect,
          start: data.playStart || state.playStart,
          stop: trialStop.getTime(),
          details: {},
          accuracy: (data.trialWasCorrect ?? state.trialWasCorrect) ? 1 : 0,
          createNewRound,
        },
        getToken()
      );

      // Update store state
      if (data.tickets) {
        setTickets(data.tickets);
      }
      setTrialWasCorrect(false);
    } catch (error) {
      console.error('Failed to save trial results:', error);
      throw error;
    }
  }

  async function saveTrialResults(
    data: Record<string, unknown>
  ): Promise<unknown> {
    return api.post('/gameplay/trials/save', data, getToken());
  }

  function incrementLevel() {
    const newLevel = state.level + 1;
    state.level = Math.min(newLevel, state.maxLevel);
  }

  function decrementLevel() {
    setLevel(Math.max(1, state.level - 1));
  }

  function resetLevel() {
    setLevel(1);
  }

  function incrementScore(amount: number) {
    setScore(state.score + amount);
  }

  function decrementScore(amount: number) {
    setScore(Math.max(0, state.score - amount));
  }

  function resetGamePlay() {
    setCurrentRound(1);
    setCurrentTrial(1);
    setScore(0);
    setTrialsComplete(0);
    setTrialsCorrect(0);
    setTrialWasCorrect(false);
    setPreviousLocations([]);
    clearTrialStatus();
    setMovedUpALevel(false);
    setChangedRound(false);
  }

  function setGameAssets(assets: GameAssets) {
    setAssets(assets);
  }

  async function initializeGame(gameData: GameData): Promise<void> {
    const audioStore = useGameAudioStore();
    const themeStore = useThemeStore();

    if (!gameData || typeof gameData !== 'object') {
      throw new Error('Invalid game data provided');
    }

    // Set game properties with type checking
    setCurrentGame(gameData.gameName);

    themeStore.setTheme({
      backgroundImage: gameData.backgroundImage,
      backgroundRepeat: gameData.backgroundRepeat,
      backgroundColor: '#FFFFFF',
      foregroundColor: '#000000',
      secondaryColor: '#000000',
      buttonColor: gameData.title,
    });

    // Set numeric properties
    setGameId(gameData.gameId);

    setAssets({
      images: { ...gameData.assets.images },
      audio: { ...gameData.assets.audio },
    });

    // Reset all game state
    resetGamePlay();
    setTrialsComplete(0);
    setPreviousLocations([]);
    clearTrialStatus();

    await audioStore.ensureAudioInitialized();

    setLoading(false);
  }

  async function exitGame() {
    const gameAudioStore = useGameAudioStore();

    console.log('[exitGame] Exiting game:', state.currentGame);
    console.log(
      '[exitGame] Keeping lastClickedGame as:',
      state.lastClickedGame
    );

    try {
      // Don't clear lastClickedGame - it should remain set to the game that was clicked
      gameAudioStore.setGameExited(true);

      console.log('[exitGame] Attempting navigation to home...');
      await navigateToHome();
      console.log('[exitGame] Navigation to home completed successfully');
    } catch (error) {
      console.error(
        '[exitGame] Navigation failed, trying fallback method:',
        error
      );

      // Fallback navigation using window.location
      try {
        window.location.href = '/';
        console.log('[exitGame] Fallback navigation initiated');
      } catch (fallbackError) {
        console.error(
          '[exitGame] Critical: All navigation methods failed:',
          fallbackError
        );
        // As a last resort, force page reload to home
        window.location.reload();
      }
    }
  }

  function getToken() {
    const userStore = useUserStore();
    // Get the token directly from localStorage to ensure it's the latest value
    let token = null;

    try {
      const authData = localStorage.getItem('user.auth');
      if (authData) {
        const parsedAuth = JSON.parse(authData);
        token = parsedAuth.token;
      }
    } catch (e) {
      console.error('Error reading token from localStorage:', e);
    }

    // Fall back to store value if localStorage fails
    if (!token && userStore.auth.token) {
      token = userStore.auth.token;
    }

    return token;
  }

  function setTickets(value: number) {
    state.tickets = value;
  }

  function setInstructions(value: Array<string | Record<string, unknown>>) {
    state.instructions = value;
  }

  return {
    // State
    state,

    // Getters
    currentGameObject,
    remainingTrials,
    remainingRounds,
    gamePlayComplete,
    isFirstPlay,
    activeComponent,

    // Actions
    startGamePlay,
    saveTrialResults,
    setGameId,
    setRoundId,
    setCurrentRound,
    setCurrentTrial,
    setLevel,
    setMaxLevel,
    setGameMode,
    setCurrentGame,
    setScore,
    setCurrentStep,
    setTimer,
    setTimerDuration,
    setAnswerTimerDuration,
    setHints,
    setTextPlayHints,
    setTrials,
    setRounds,
    setAssets,
    setMovedUpALevel,
    setTrialWasCorrect,
    setTrialsCorrect,
    setLoading,
    setTrialsComplete,
    setPreviousLocations,
    setTrialStatus,
    clearTrialStatus,
    setCorrectAnswers,
    setAnswerSequence,
    setCurrentGameScore,
    setQueryColors,
    setInMission,
    setGames,
    setLastClickedGame,
    setStopTimer,
    setProgress,
    setScoreDownIncorrect,
    setScoreUpTrial,
    incrementScore,
    decrementScore,
    resetGamePlay,
    setGameAssets,
    exitGame,
    initializeGame,
    getTrialClasses,
    leaveGame,
    incrementLevel,
    decrementLevel,
    resetLevel,
    incrementTrialsCorrect,
    updatePlayLevel,
    addToCorrectAnswers,
    resetCorrectAnswers,
    processTrialAndAdvance,

    // Direct property access (computed)
    level,
    gameId,
    roundId,
    currentRound,
    currentTrial,
    maxLevel,
    gameMode,
    currentGame,
    score,
    currentStep,
    timer,
    timerDuration,
    answerTimerDuration,
    hints,
    textPlayHints,
    trials,
    rounds,
    assets,
    movedUpALevel,
    trialWasCorrect,
    trialsCorrect,
    loading,
    trialsComplete,
    previousLocations,
    trialStatus: computed(() => state.trialStatus),
    correctAnswers,
    answerSequence,
    currentGameScore,
    query_colors,
    inMission,
    games,
    lastClickedGame,
    stopTimer,
    progress,
    scoreDownIncorrect,
    scoreUpTrial,
    typedChars,
    modalMessage,
    correctThisRound,
    instructions,
    // Methods
    startNewRound,
    setTickets,
    setInstructions,
    setGameLevelAndSaveTrialResults,
    updateGameLevel,
    addToPreviousLocations,
    clearPreviousLocations,
    addToAnswerSequence,
    removeLastNumberAnswerInSequence,
    clearAnswerSequence,
    setTypedChars,
    setModalMessage,
    setCorrectThisRound,
    incrementCorrectThisRound,
    getUserLevelAndSettings,
  };
});
